import { NgStyle } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '@app/app.config';
import { SuperUserService } from '@app/auth/components/login/services/super-user.service';
import { UDDItem, User, UserService } from '@app/core/auth';
import { ApiListResponse } from '@app/core/backend';
import { UploadsService } from '@app/pages/dashboard/pages/uploads/services/uploads.service';
import { EmptyDialogComponent } from '@app/shared/dialogs/emptyDialog/empty-dialog.component';
import {
	AutocompleteInput,
	BaseInput,
	Form,
	SelectOption
} from '@app/shared/form';
import { FormComponent } from '@app/shared/form/components';
import { I18nService } from '@eng-ds/translate';
import { NbSecurityModule } from '@nebular/security';
import {
	NbActionsModule,
	NbButtonModule,
	NbDialogRef,
	NbDialogService,
	NbIconModule,
	NbMediaBreakpointsService,
	NbMenuItem,
	NbMenuService,
	NbSidebarService,
	NbThemeService
} from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { UserActionComponent } from './user-action/user.component';

@UntilDestroy()
@Component({
	selector: 'app-header',
	styleUrls: ['./header.component.scss'],
	templateUrl: './header.component.html',
	standalone: true,
	imports: [
		FormComponent,
		NbActionsModule,
		NbButtonModule,
		NbIconModule,
		NbSecurityModule,
		NgStyle,
		TranslateModule,
		UserActionComponent
	]
})
export class HeaderComponent implements OnInit {
	userPictureOnly = false;

	userMenu: NbMenuItem[] = [
		{ title: 'Profile' },
		{
			title: 'Log out',
			link: AppConfig.authentication.loginUrl,
			skipLocationChange: true
		}
	];

	isBadgeable = false;

	@ViewChild('selectUddDialogTemplate')
	selectUddDialogTemplate: TemplateRef<any>;
	dialogRef: NbDialogRef<any>;
	superUserForm: Form;
	optionsSU: SelectOption<string, string>[] = [];

	constructor(
		private sidebarService: NbSidebarService,
		private menuService: NbMenuService,
		private themeService: NbThemeService,
		private uploadsService: UploadsService,
		private userService: UserService,
		private dialogService: NbDialogService,
		private superUserService: SuperUserService,
		private i18n: I18nService,
		private breakpointService: NbMediaBreakpointsService,
		private router: Router
	) {
		this.uploadsService.changeBadgeable.subscribe(b => {
			this.isBadgeable = b;
		});
	}

	get user(): User {
		return this.userService.user;
	}

	ngOnInit() {
		const { xl } = this.breakpointService.getBreakpointsMap();
		this.themeService
			.onMediaQueryChange()
			.pipe(
				map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
				untilDestroyed(this)
			)
			.subscribe(
				(isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl)
			);

		this._initSuperUserForm();
	}

	private _initSuperUserForm() {
		this.superUserForm = new Form({
			header: { show: false },
			controls: {
				userToPersonify: new AutocompleteInput<
					{ PartitaIvaUdD: string; RagSocialeUdD: string },
					string
				>({
					options: this.userService
						.getUddList()
						.pipe(
							map(values => this._mapUddListToLabelValue(values))
						),
					label: this.i18n.translate('LOGIN.USER_TO_PERSONIFY.LABEL'),
					placeholder: this.i18n.translate(
						'LOGIN.USER_TO_PERSONIFY.PLACEHOLDER'
					),
					size: '12|12|12|12|12'
				}) as BaseInput<any>
			}
		});
	}

	private _mapUddListToLabelValue(
		uddList: ApiListResponse<UDDItem>
	): SelectOption<
		{ PartitaIvaUdD: string; RagSocialeUdD: string },
		string
	>[] {
		return uddList.Body.map(udd => {
			return {
				label: `${udd.PartitaIva} - ${udd.RagioneSociale}`,
				value: {
					PartitaIvaUdD: udd.PartitaIva.toString(),
					RagSocialeUdD: udd.RagioneSociale
				}
			};
		});
	}

	toggleSidebar(): boolean {
		this.sidebarService.toggle(true, 'menu-sidebar');

		return false;
	}

	navigateHome() {
		this.menuService.navigateHome();
		return false;
	}

	navigateUploads() {
		this.router.navigate(['/dashboard/caricamenti']);
	}

	onOpenDialogSelectUdd() {
		this.dialogRef = this.dialogService.open(EmptyDialogComponent, {
			context: {
				dialogTemplate: this.selectUddDialogTemplate,
				emptyToolbar: true,
				styleDialog: { width: '900px', height: '420px' }
			}
		});
	}

	onSaveUdd() {
		const { PartitaIvaUdD, RagSocialeUdD } = this.superUserForm.value
			?.userToPersonify || {
			...this.superUserForm.value?.userToPersonify
		};
		const user: User = {
			...this.userService.user,
			PartitaIvaUdD,
			RagSocialeUdD
		};
		this.userService.saveUser(user);
		this.dialogRef.close();
	}

	get isBadge() {
		return this.isBadgeable;
	}
}
